/**
===================================================
Module: frontend.src.components.visualizer.ExportButton.tsx
Description: Export functionality component for VSLAM data
Owner: Kawada Technosystem Co Ltd
All Rights Reserved
This script is proprietary and confidential.
Unauthorized copying, distribution, or modification of this
file, via any medium, is strictly prohibited.
===================================================
*/
import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { apiService } from "../../services/api";
import { useVisualizer } from "../../contexts/VisualizerContext";
import { LoadingPhase } from "../../types/visualizer";

const ExportButton: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { state, addSuccess, addError } = useVisualizer();

  const [showDropdown, setShowDropdown] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async (format: "gpx" | "kml" | "csv") => {
    if (!sessionId) return;

    setIsExporting(true);
    setShowDropdown(false);

    try {
      await apiService.exportTrajectory(sessionId, format);
      addSuccess(
        "ダウンロードが開始されました",
      );
    } catch (error) {
      addError(
        `ダウンロードに失敗しました`,
      );
    } finally {
      setIsExporting(false);
    }
  };

  const handleClickOutside = useCallback((event: MouseEvent) => {
    const target = event.target as HTMLElement;
    if (!target.closest(".export-dropdown-container")) {
      setShowDropdown(false);
    }
  }, []);

  useEffect(() => {
    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showDropdown, handleClickOutside]);

  const isDisabled = isExporting || state.loadingPhase !== LoadingPhase.READY;

  return (
    <div className="export-button-container">
      <div className="export-dropdown-container">
        <button
          className={`export-button ${isExporting ? "exporting" : ""}`}
          onClick={() => setShowDropdown(!showDropdown)}
          disabled={isDisabled}
          title="移動軌跡のダウンロード"
        >
          {isExporting ? (
            <div className="export-spinner" />
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
              <path d="M12,19L16,15H13V10H11V15H8L12,19Z" />
            </svg>
          )}
        </button>

        {showDropdown && (
          <div className="export-dropdown">
            <button
              className="export-option"
              onClick={() => handleExport("gpx")}
            >
              <span className="export-format">GPX</span>
              <span className="export-description">GPS データ交換形式</span>
            </button>
            <button
              className="export-option"
              onClick={() => handleExport("kml")}
            >
              <span className="export-format">KML</span>
              <span className="export-description">Google Earth形式</span>
            </button>
            <button
              className="export-option"
              onClick={() => handleExport("csv")}
            >
              <span className="export-format">CSV</span>
              <span className="export-description">カンマ区切りテキスト</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExportButton;
